import type { EntryCollection, ContentType } from 'contentful';

import { ReactElement } from 'react';
import cmsClient from '../../lib/cms/client';
import reviewIncClient from '../../lib/reviewinc/client';
import hydrateTemplate from '../../lib/cms/links';
import {
  toSlug,
  getServerSidePropsWithErrors,
  getContentfulPageData,
  cacheDirective,
  getContentfulSiteData,
  isInvalid,
  withSiteData,
} from '../../lib/routing';
import { resolveBreadcrumbSlugs } from '../../lib/cms/breadcrumbs';
import {
  IComponentProductCarousel,
  IFragmentInsuranceFields,
  IMetaLocationsSharedDataFields,
  IPageDoctorFields,
  IPageLocation,
  IPageLocationFields,
  IPageFields,
  IProductVariant,
} from 'types/contentful';
import { removeIfUndefined, doctorDisplayName, formatCityName } from 'lib/util';
import { JsonObject } from 'lib/json';
import { LOCATION_SELECT_FIELDS } from 'lib/consts';
import { getReviewAverage, getReviewsWithComments, reviewFragmentToRequest } from 'lib/reviewinc';
import { buildComponent } from 'lib/cms/links';
import { translateServerSideComponent, DynamicComponentTypes, otherParams } from 'components';
import PageLayout, { PageProps } from 'components/UI/Doctor/Layout/PageLayout';
import DoctorLayout, { DoctorProps } from 'components/UI/Doctor/Layout/DoctorLayout';

type ToRecord<T> = Record<keyof T, T[keyof T]>;

type CombinedProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> =
  | PageProps<K, CP>
  | DoctorProps<K, CP>;

export default function DoctorOrPage<K extends DynamicComponentTypes, CP>(
  props: CombinedProps<K, CP>
): ReactElement {
  if (props.pageType === 'page') {
    return <PageLayout {...props} />;
  }
  return <DoctorLayout {...props} />;
}

export const getServerSideProps = getServerSidePropsWithErrors<CombinedProps>(
  async ({ resolvedUrl, params, query, res, req, preview }) => {
    const slug = toSlug(params?.slug) || '';
    const host = req.headers.host;
    const siteData = await getContentfulSiteData({ preview, resolvedUrl, req, res });

    if (isInvalid(siteData)) {
      return siteData;
    }

    // Check if slug is a valid type (optometrists or ophthalmologists)
    const slugParts = slug.split('/');
    const firstPart = slugParts[0]?.toLowerCase();
    const validTypes = ['optometrists', 'ophthalmologists'];

    // Handle page type pages doctors/*/...
    if (firstPart && validTypes.includes(firstPart)) {
      const pageSlug = `doctors/${slug}`;

      const queryOptions = {
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': host,
        'fields.slug': pageSlug,
        content_type: 'page',
        include: 10, // Make sure we retrieve nav with links
      };

      const contentfulData = await getContentfulPageData<IPageFields>({
        res,
        req,
        preview,
        queryOptions,
      });

      if (isInvalid(contentfulData)) {
        return withSiteData(contentfulData, siteData);
      }

      const client = cmsClient(preview);
      const pageMeta = contentfulData.item.fields;

      // fetch template
      const rawTemplate: ContentType = await client.getContentType(
        pageMeta.template.sys.contentType.sys.id
      );

      // remove symbol fields
      const templateLayout = rawTemplate.fields.filter((field) => field.type !== 'Symbol');

      const searchspringSiteId = siteData?.ecommIds?.fields?.searchSpringId || '';
      query.siteId = searchspringSiteId;
      const freshPaintToken = siteData?.freshPaintToken || '';

      const otherParamsData: otherParams = {
        query,
        req,
        res,
        resolvedUrl,
        searchspringSiteId,
        freshPaintToken,
      };
      // hydrate and build out template requirements
      const hydrated = await Promise.all(
        hydrateTemplate(
          templateLayout,
          pageMeta.template.fields as ToRecord<typeof pageMeta.template.fields>,
          contentfulData.includes?.Asset || [],
          contentfulData.includes?.Entry || []
        ).map((item) => translateServerSideComponent(item, !!preview, otherParamsData))
      );

      // Build breadcrumbs from slug (e.g. doctors/optometrists/illinois)
      const pageSlugParts = pageSlug.split('/').filter(Boolean);
      const pageBreadcrumbs =
        pageSlugParts.length <= 1
          ? []
          : [
              { slug: '/doctors', label: 'Doctors' },
              ...pageSlugParts.slice(1).map((part, i) => ({
                slug: '/' + pageSlugParts.slice(0, i + 2).join('/'),
                label: formatDoctorBreadcrumbLabel(part),
              })),
            ];

      const resolvedPageBreadcrumbs = await resolveBreadcrumbSlugs({
        breadcrumbs: pageBreadcrumbs,
        siteHost: host || '',
        preview: !!preview,
      });

      res.setHeader('Cache-Control', cacheDirective(preview));

      return {
        props: {
          pageType: 'page' as const,
          noIndexNoFollow: !!pageMeta.doNotIndex,
          preview: !!preview,
          siteData,
          title: pageMeta.title || '',
          description: pageMeta.description || '',
          content: hydrated,
          currentSlug: pageSlug,
          pageDates: {
            datePublished: contentfulData.item.sys.createdAt,
            dateModified: contentfulData.item.sys.updatedAt,
          },
          hideTopNavCtas: pageMeta.hideTopNavCtas || false,
          ...(pageMeta.jsonSchema ? { jsonSchema: pageMeta.jsonSchema as JsonObject } : {}),
          otherParams: {
            query,
            resolvedUrl,
            searchspringSiteId,
            freshPaintToken: freshPaintToken || undefined,
          },
          breadcrumbs: resolvedPageBreadcrumbs,
        },
      };
    }

    // Handle doctor pages: doctors/*
    const queryOptions = {
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': host,
      'fields.slug': slug,
      content_type: 'pageDoctor',
      include: 10,
    };

    const contentfulData = await getContentfulPageData<IPageDoctorFields>({
      res,
      req,
      preview,
      queryOptions,
      redirectInsteadofNotFound: '/locations',
    });

    if (isInvalid(contentfulData)) {
      return withSiteData(contentfulData, siteData);
    }

    const client = cmsClient(preview);
    const content = contentfulData.item.fields;

    // sanitize deep nested frames -> products -> locationIds -> doctors to avoid serialization issues
    const sanitizeFrames = (frames: IComponentProductCarousel): IComponentProductCarousel => {
      if (!frames?.fields?.products) return frames;
      const cleanedProducts = frames.fields.products.map(
        (prod: IProductVariant): IProductVariant => {
          const p = { ...prod };
          if (p.fields && 'locationIds' in p.fields) {
            delete p.fields.locationIds;
          }
          return p;
        }
      );
      return {
        ...frames,
        fields: {
          ...frames.fields,
          products: cleanedProducts,
        },
      };
    };

    let officeInfo: IPageLocationFields | null = null;

    if (content.primaryLocation !== undefined || query?.office !== undefined) {
      // google bot is adding a trailing forward slash so we now check for one and remove it
      // locations slugs in contentful are prevented from having a trailing forward slash
      const slug = query?.office
        ? decodeURIComponent(query?.office as string).replace(/\/$/, '')
        : content.primaryLocation?.fields.slug;

      const locationPage: EntryCollection<IPageLocationFields> = await client.getEntries({
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': host,
        'fields.slug': slug,
        select: 'fields.hours,fields.hoursNotes,' + LOCATION_SELECT_FIELDS,
        content_type: 'pageLocation',
        include: 1,
      });

      if (locationPage.items.length === 1) {
        officeInfo = { ...locationPage.items[0].fields };
      }
    }

    const cleanSecondaryLocations = content.secondaryLocations
      ? extractFieldsWithoutCircularDoctorDependency(content.secondaryLocations)
      : [];
    const cleanPrimaryLocations = content.primaryLocation
      ? extractFieldsWithoutCircularDoctorDependency([content.primaryLocation])
      : [];

    // delete circular dependency
    delete content.primaryLocation;
    delete content.secondaryLocations;

    // deep-clean frames branch if present
    if (content.frames) {
      content.frames = sanitizeFrames(content.frames);
    }

    const sharedLocationData: EntryCollection<IMetaLocationsSharedDataFields> = await client.getEntries(
      {
        'fields.site.sys.contentType.sys.id': 'site',
        'fields.site.fields.id': host,
        select:
          'fields.paymentsAccepted,fields.closedDates,fields.scheduleApptCtaText,fields.insuranceDisclaimer,fields.insuranceSlug,fields.hideEyeIcon,fields.markerPinIcon,fields.billingInquirySlug,fields.insuranceProviders',
        content_type: 'metaLocationsSharedData',
        include: 1,
      }
    );

    const doctorVideo = content.doctorVideo
      ? await Promise.all(
          [content.doctorVideo]
            .map((item) => buildComponent(item?.sys?.contentType?.sys?.id, item?.fields))
            .map((item) => translateServerSideComponent(item, !!preview))
        )
      : null;

    const reviewAvg =
      content.reviewsIds &&
      (await getReviewAverage(reviewIncClient, reviewFragmentToRequest(content.reviewsIds)));

    const reviews =
      content.reviewsIds &&
      (await getReviewsWithComments(reviewIncClient, reviewFragmentToRequest(content.reviewsIds)));

    const doctorInsuranceProviders = content.insuranceProviders || [];
    const sharedInsuranceProviders = sharedLocationData?.items[0]?.fields?.insuranceProviders || [];

    let finalInsuranceProviders: IFragmentInsuranceFields[] = doctorInsuranceProviders.map(
      (item) => item.fields
    );

    if (!finalInsuranceProviders.length) {
      finalInsuranceProviders = sharedInsuranceProviders.map((item) => item.fields);
    }

    // Sort insurance providers alphabetically by insurer name (case-insensitive)
    finalInsuranceProviders.sort((a, b) => {
      const nameA = (a.insurer || '').toLowerCase();
      const nameB = (b.insurer || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const resolvedDoctorBreadcrumbs = await resolveBreadcrumbSlugs({
      breadcrumbs: [
        { slug: '/doctors', label: 'Doctors' },
        { slug: '', label: doctorDisplayName(content) },
      ],
      siteHost: host || '',
      preview: !!preview,
    });

    res.setHeader('Cache-Control', cacheDirective(preview));

    return {
      props: {
        pageType: 'doctor' as const,
        preview: preview ? true : false,
        officeInfo,
        ...removeIfUndefined({ closedDates: sharedLocationData?.items[0]?.fields?.closedDates }),
        paymentsAccepted: sharedLocationData?.items[0]?.fields?.paymentsAccepted || [],
        scheduleApptCtaText: sharedLocationData?.items[0]?.fields?.scheduleApptCtaText || '',
        hideEyeOnPin: !!sharedLocationData?.items[0]?.fields?.hideEyeIcon,
        secondaryLocations: [...cleanPrimaryLocations, ...cleanSecondaryLocations],
        markerIcon: sharedLocationData?.items[0]?.fields?.markerPinIcon || '',
        insuranceDisclaimer: sharedLocationData?.items[0]?.fields?.insuranceDisclaimer || '',
        insuranceSlug: sharedLocationData?.items[0]?.fields?.insuranceSlug || '',
        billingInquirySlug: sharedLocationData?.items[0]?.fields?.billingInquirySlug || '',
        insuranceProviders: finalInsuranceProviders,
        siteData,
        content,
        ...removeIfUndefined({ reviewAvg, reviews }),
        currentSlug: `doctors/${slug}`,
        pageDates: {
          datePublished: contentfulData.item.sys.createdAt,
          dateModified: contentfulData.item.sys.updatedAt,
        },
        doctorVideo,
        breadcrumbs: resolvedDoctorBreadcrumbs,
      },
    };
  }
);

function formatDoctorBreadcrumbLabel(part: string): string {
  const lower = part.toLowerCase();
  if (lower === 'optometrists') return 'Optometrists';
  if (lower === 'ophthalmologists') return 'Ophthalmologists';
  return formatCityName(part);
}

function extractFieldsWithoutCircularDoctorDependency(
  data: IPageLocation[]
): IPageLocationFields[] {
  return data.map((item) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { doctors, ...fieldsWithoutDoctors } = item.fields;
    return fieldsWithoutDoctors;
  });
}
